/* Only Coppermine's native gift card and its optional physical card are paired. */
(() => {
  'use strict';
  const configElement = document.getElementById('coppermine-gift-shipping-config');
  if (!configElement || window.CoppermineGiftShipping) return;
  const config = JSON.parse(configElement.textContent);
  const giftIds = new Set(config.giftVariants.map(String));
  const physicalId = Number(config.physicalVariant);
  const root = window.Shopify.routes.root;
  const isGift = id => giftIds.has(String(id));
  const isPhysical = item => Number(item.variant_id) === physicalId;
  const relevant = cart => cart.items.some(item => isGift(item.variant_id) || isPhysical(item));
  const deliveryProperty = '_cm_physical_card';
  const wantsPhysical = item => item.properties?.[deliveryProperty] !== 'no';
  let latestCart;
  let hasGift = config.hasGift;
  let pending = Promise.resolve();
  let busy = 0;
  let checkoutReady = false;

  function remember(cart) {
    latestCart = cart;
    hasGift = relevant(cart);
    document.documentElement.classList.toggle('cm-gift-cart', hasGift);
    renderCartChoices();
    return cart;
  }
  document.documentElement.classList.toggle('cm-gift-cart', hasGift);

  async function request(path, body) {
    const response = await fetch(root + path, body ? {
      method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body)
    } : {headers: {Accept: 'application/json'}, cache: 'no-store'});
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Unable to prepare your physical gift card. Please try again.');
    return result;
  }
  function showError(error) {
    console.error('Gift-card shipping:', error);
    const message = error.message || 'Unable to prepare your physical gift card. Please try again.';
    if (typeof window.showWarning === 'function') window.showWarning(message);
    else window.alert(message);
  }
  function queued(action) {
    busy += 1;
    const result = pending.catch(() => {}).then(action);
    pending = result.finally(() => { busy -= 1; });
    // Keep a failed request from producing an unhandled rejection in the queue.
    pending.catch(() => {});
    return result;
  }

  async function reconcile(cart) {
    const parents = cart.items.filter(item => isGift(item.variant_id));
    const physicals = cart.items.filter(isPhysical);
    if (!parents.length && !physicals.length) return remember(cart);
    const updates = {};
    const additions = [];
    const kept = new Set();
    for (const parent of parents.filter(wantsPhysical)) {
      const children = physicals.filter(item => item.parent_relationship?.parent_key === parent.key);
      const child = children[0];
      if (child) {
        kept.add(child.key);
        if (child.quantity !== parent.quantity) updates[child.key] = parent.quantity;
      } else {
        additions.push({id: physicalId, quantity: parent.quantity, parent_line_key: parent.key});
      }
    }
    for (const item of physicals) if (!kept.has(item.key)) updates[item.key] = 0;
    if (Object.keys(updates).length) cart = await request('cart/update.js', {updates});
    if (additions.length) {
      await request('cart/add.js', {items: additions});
      cart = await request('cart.js');
    }
    // Fail closed if Shopify could not create a matching shippable line.
    for (const parent of cart.items.filter(item => isGift(item.variant_id) && wantsPhysical(item))) {
      const child = cart.items.find(item => isPhysical(item) && item.parent_relationship?.parent_key === parent.key);
      if (!child || child.quantity !== parent.quantity || !child.requires_shipping || child.gift_card || child.final_price !== 0) {
        throw new Error('The physical gift card could not be prepared. Please try again before checking out.');
      }
    }
    for (const child of cart.items.filter(isPhysical)) {
      if (!cart.items.some(parent => isGift(parent.variant_id) && wantsPhysical(parent) && parent.key === child.parent_relationship?.parent_key)) {
        throw new Error('The physical card could not be removed. Please try again before checking out.');
      }
    }
    return remember(cart);
  }
  function sync(cart) {
    if (!busy && !relevant(cart)) return Promise.resolve(remember(cart));
    return queued(async () => reconcile(await request('cart.js')));
  }
  function add(id, quantity, properties = {}) {
    hasGift = true;
    document.documentElement.classList.add('cm-gift-cart');
    return queued(async () => {
      const result = await request('cart/add.js', {items: [{id: Number(id), quantity: Number(quantity) || 1, properties}]});
      await reconcile(await request('cart.js'));
      return result.items[0];
    });
  }
  function propertiesFrom(form) {
    const choice = document.querySelector('[data-cm-product-delivery]:checked');
    const properties = {[deliveryProperty]: choice?.value === 'email' ? 'no' : 'yes'};
    if (!form) return properties;
    for (const [name, value] of new FormData(form)) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    return properties;
  }

  // Preserve Ella's normal cart drawer and callbacks for all other merchandise.
  const originalAdd = Shopify.addItem;
  Shopify.addItem = function(id, quantity, target, callback, input) {
    if (!isGift(id)) return originalAdd.apply(this, arguments);
    return add(id, quantity, propertiesFrom(target?.closest('form')?.[0]))
      .then(item => typeof callback === 'function' ? callback(item) : Shopify.onItemAdded(item))
      .catch(showError).finally(() => target?.removeClass('is-loading'));
  };
  const originalGet = Shopify.getCart;
  Shopify.getCart = function(callback) {
    return originalGet.call(this, cart => {
      if (!relevant(cart) && !busy) {
        remember(cart);
        return typeof callback === 'function' ? callback(cart) : Shopify.onCartUpdate(cart);
      }
      sync(cart).then(updated => typeof callback === 'function' ? callback(updated) : Shopify.onCartUpdate(updated)).catch(showError);
    });
  };
  for (const method of ['changeItem', 'removeItem']) {
    const original = Shopify[method];
    Shopify[method] = function(...args) {
      if (!hasGift && !busy) return original.apply(this, args);
      const callback = args[method === 'changeItem' ? 3 : 2];
      const quantity = method === 'changeItem' ? Number(args[1]) : 0;
      return queued(async () => {
        const changed = await request('cart/change.js', {id: String(args[0]), quantity});
        const updated = await reconcile(changed);
        if (typeof callback === 'function') callback(updated);
        else Shopify.onCartUpdate(updated);
      }).catch(showError);
    };
  }
  // Store the choice on the native gift-card line so reloads and checkout keep it.
  // Preserve all existing properties, including Shopify's recipient details.
  function setDelivery(parentKey, physical) {
    return queued(async () => {
      const cart = await request('cart.js');
      const parent = cart.items.find(item => item.key === parentKey && isGift(item.variant_id));
      if (!parent) throw new Error('Your cart has changed. Please refresh and try again.');
      const changed = await request('cart/change.js', {
        id: parent.key, quantity: parent.quantity,
        properties: {...parent.properties, [deliveryProperty]: physical ? 'yes' : 'no'}
      });
      return reconcile(changed);
    });
  }
  function renderCartChoices() {
    document.querySelectorAll(`[data-cart-quantity-id="${physicalId}"]`).forEach(input => {
      input.readOnly = true;
      input.setAttribute('aria-label', 'Physical card quantity matches its gift card');
    });
    if (!latestCart) return;
    document.querySelectorAll('[data-item-row]').forEach(row => {
      const key = row.dataset.line || row.querySelector('[data-cart-quantity][data-line]')?.dataset.line;
      const parent = latestCart.items.find(item => item.key === key && isGift(item.variant_id));
      const container = row.querySelector('.cart-item-block-right');
      if (!parent || !container) return;
      const physical = wantsPhysical(parent);
      let panel = container.querySelector('[data-cm-cart-delivery]');
      if (panel && panel.dataset.parentKey === key && panel.dataset.delivery === String(physical)) return;
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'cm-gift-delivery cm-gift-cart-choice';
        panel.setAttribute('data-cm-cart-delivery', '');
        container.appendChild(panel);
      }
      panel.dataset.parentKey = key;
      panel.dataset.delivery = String(physical);
      panel.innerHTML = physical
        ? '<strong>Physical card + emailed code</strong><p>The card is free. <b>Shipping costs extra at checkout.</b> Shipping does not increase your gift-card balance.</p><button type="button" class="cm-gift-choice-button" data-cm-remove>Remove physical card — email only</button><div data-cm-remove-warning hidden role="alert"><p><b>You will not receive a physical card.</b> Your code will still arrive by email. No shipping charge for this gift card; shipping for other physical items still applies.</p><button type="button" class="cm-gift-choice-button" data-cm-confirm-remove>Confirm email only</button> <button type="button" class="cm-gift-choice-button cm-gift-secondary" data-cm-keep>Keep physical card</button></div>'
        : '<strong>Email code only — no physical card</strong><p>No shipping charge for this gift card. Shipping for other physical items still applies.</p><button type="button" class="cm-gift-choice-button" data-cm-restore>Add physical card — shipping applies</button>';
    });
  }
  function showRemovalWarning(parentKey) {
    const panels = [...document.querySelectorAll('[data-cm-cart-delivery]')].filter(panel => panel.dataset.parentKey === parentKey);
    panels.forEach(panel => {
      panel.querySelector('[data-cm-remove]').hidden = true;
      panel.querySelector('[data-cm-remove-warning]').hidden = false;
    });
    const visible = panels.find(panel => panel.getClientRects().length);
    visible?.querySelector('[data-cm-confirm-remove]').focus();
  }
  function applyDelivery(button, parentKey, physical) {
    button.disabled = true;
    setDelivery(parentKey, physical).then(() => window.location.reload())
      .catch(error => { button.disabled = false; showError(error); });
  }
  renderCartChoices();
  new MutationObserver(renderCartChoices).observe(document.body, {childList: true, subtree: true});
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-cm-product-delivery]')) return;
    document.querySelectorAll('[data-cm-email-warning]').forEach(warning => {
      warning.hidden = event.target.value !== 'email';
    });
  });

  // Catch native form submissions and recipient-form purchases, which use a
  // separate Ella code path. Ordinary product forms are untouched.
  function handleGiftForm(event, form, button) {
    if (!form || !isGift(new FormData(form).get('id'))) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button?.disabled || form.dataset.cmAdding === 'true') return true;
    if (!form.reportValidity()) return true;
    form.dataset.cmAdding = 'true';
    if (button) button.disabled = true;
    const data = new FormData(form);
    add(data.get('id'), data.get('quantity') || 1, propertiesFrom(form))
      .then(() => { window.location.href = root + 'cart'; })
      .catch(showError)
      .finally(() => { delete form.dataset.cmAdding; if (button) button.disabled = false; });
    return true;
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form instanceof HTMLFormElement && new URL(form.action).pathname.endsWith('/cart/add')) {
      handleGiftForm(event, form, event.submitter);
    }
  }, true);
  document.addEventListener('click', event => {
    const choice = event.target.closest('[data-cm-remove], [data-cm-keep], [data-cm-confirm-remove], [data-cm-restore]');
    if (choice) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const panel = choice.closest('[data-cm-cart-delivery]');
      if (choice.disabled) return;
      if (choice.hasAttribute('data-cm-remove')) showRemovalWarning(panel.dataset.parentKey);
      else if (choice.hasAttribute('data-cm-keep')) {
        panel.querySelector('[data-cm-remove]').hidden = false;
        panel.querySelector('[data-cm-remove-warning]').hidden = true;
      } else applyDelivery(choice, panel.dataset.parentKey, choice.hasAttribute('data-cm-restore'));
      return;
    }
    const physicalRemove = event.target.closest(`[data-cart-remove-id="${physicalId}"]`);
    if (physicalRemove) {
      event.preventDefault();
      event.stopImmediatePropagation();
      queued(async () => remember(await request('cart.js'))).then(cart => {
        const child = cart.items.find(item => item.key === physicalRemove.dataset.line && isPhysical(item));
        if (child?.parent_relationship) showRemovalWarning(child.parent_relationship.parent_key);
      }).catch(showError);
      return;
    }
    const button = event.target.closest('[data-btn-addtocart], #product-add-to-cart, #product-sticky-add-to-cart');
    if (button && handleGiftForm(event, button.closest('form'), button)) return;
    const checkout = event.target.closest('#cart-checkout, #cart-sidebar-checkout, a[href$="/checkout"], button[name="checkout"]');
    if (!checkout || checkout.disabled || checkoutReady || (!hasGift && !busy)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout.disabled = true;
    queued(async () => reconcile(await request('cart.js')))
      .then(() => {
        checkoutReady = true;
        checkout.disabled = false;
        checkout.click(); // retain Ella's cart note, coupon, and checkout behavior
        checkoutReady = false;
      }).catch(error => { checkout.disabled = false; showError(error); });
  }, true);

  window.CoppermineGiftShipping = {isGift, sync, setDelivery};
  // Reconcile restored carts before checkout, including gift cards added before
  // this feature was enabled. Reload only if visible line quantities changed.
  if (hasGift) {
    queued(async () => {
      const before = await request('cart.js');
      const signature = cart => JSON.stringify(cart.items.map(item => [item.key, item.quantity]));
      const after = await reconcile(before);
      if (signature(before) !== signature(after)) window.location.reload();
    }).catch(showError);
  }
})();
